import { Vector3, Quaternion } from 'three';

const newPose = () => ({ position: new Vector3(), quaternion: new Quaternion() });
const copyPose = p => {
  const v = p?.position, q = p?.quaternion;
  if (!v?.isVector3 || !q?.isQuaternion || ![v.x, v.y, v.z, q.x, q.y, q.z, q.w].every(Number.isFinite) || !(q.lengthSq() > 0)) throw new TypeError('pose must have a finite position and nonzero finite quaternion');
  return { position: v.clone(), quaternion: q.clone().normalize() };
};
const interpolate = (out, a, b, t) => {
  out.position.lerpVectors(a.position, b.position, t);
  out.quaternion.slerpQuaternions(a.quaternion, b.quaternion, t);
};
const ease = t => t * t * (3 - 2 * t);

/**
 * Create a deterministic gesture. Poses are {position: Vector3, quaternion:
 * Quaternion}, in world space except `grip`, which is the hand pose in the
 * object's local frame. All poses, including rest poses, must be supplied.
 * durations = [approach, carry, retreat] in seconds, each strictly positive.
 * sample(elapsedSeconds) clamps time, reuses its returned poses on every call,
 * and never changes inputs. Copy the result if it must survive another sample.
 * The caller keeps all other objects untouched and applies poses to its scene.
 */
export function createArmMotion({ source, destination, startHand, endHand, grip, durations }) {
  if (!Array.isArray(durations) || durations.length !== 3 || !durations.every(n => Number.isFinite(n) && n > 0) || !Number.isFinite(durations[0] + durations[1] + durations[2])) throw new RangeError('durations must contain three finite positive seconds');
  const s = copyPose(source), d = copyPose(destination), start = copyPose(startHand), end = copyPose(endHand), g = copyPose(grip);
  const contact = p => ({ position: g.position.clone().applyQuaternion(p.quaternion).add(p.position), quaternion: p.quaternion.clone().multiply(g.quaternion) });
  const atSource = contact(s), atDestination = contact(d);
  const [approach, carry, retreat] = durations;
  const total = approach + carry + retreat;
  const out = { hand: newPose(), object: newPose(), phase: '' };
  return {
    duration: total,
    sample(elapsed) {
      if (!Number.isFinite(elapsed)) throw new RangeError('time must be finite');
      const t = Math.max(0, Math.min(total, elapsed));
      if (t < approach) {
        out.phase = 'approach';
        interpolate(out.object, s, s, 0);
        interpolate(out.hand, start, atSource, ease(t / approach));
      } else if (t < approach + carry) {
        out.phase = 'carry';
        interpolate(out.object, s, d, ease((t - approach) / carry));
        out.hand.position.copy(g.position).applyQuaternion(out.object.quaternion).add(out.object.position);
        out.hand.quaternion.copy(out.object.quaternion).multiply(g.quaternion);
      } else {
        out.phase = 'retreat';
        interpolate(out.object, d, d, 0);
        interpolate(out.hand, atDestination, end, ease((t - approach - carry) / retreat));
      }
      return out;
    }
  };
}

/**
 * Two rigid lengths in world units. solve(shoulder, target, bendDirection)
 * returns reused world-space elbow and wrist Vector3s, reached, and error
 * (distance from target). bendDirection is a world-space preferred direction,
 * not a point. Unreachable targets are clamped to the reachable annulus;
 * callers must resolve nonzero error before attaching a hand to an object.
 * At singular straight/folded poses the bend plane is mathematically ambiguous.
 */
export function createTwoBoneSolver(upper, lower) {
  if (!(Number.isFinite(upper) && upper > 0 && Number.isFinite(lower) && lower > 0)) throw new RangeError('bone lengths must be finite and positive');
  const axis = new Vector3(), bend = new Vector3();
  const result = { elbow: new Vector3(), wrist: new Vector3(), reached: false, error: 0 };
  return { solve(shoulder, target, bendDirection) {
    axis.subVectors(target, shoulder);
    const distance = axis.length();
    if (distance > 0) axis.multiplyScalar(1 / distance); else axis.set(1, 0, 0);
    const min = Math.abs(upper - lower), max = upper + lower;
    const d = Math.max(min, Math.min(max, distance));
    result.reached = distance >= min && distance <= max;
    result.error = Math.abs(distance - d);
    result.wrist.copy(axis).multiplyScalar(d).add(shoulder);
    bend.copy(bendDirection).addScaledVector(axis, -bendDirection.dot(axis));
    if (bend.lengthSq() < 1e-20) {
      if (Math.abs(axis.y) < 0.9) bend.set(0, 1, 0); else bend.set(1, 0, 0);
      bend.addScaledVector(axis, -bend.dot(axis));
    }
    bend.normalize();
    const along = d > 0 ? ((upper - lower) * (upper + lower) / d + d) / 2 : 0;
    const height = Math.sqrt(Math.max(0, upper * upper - along * along));
    result.elbow.copy(shoulder).addScaledVector(axis, along).addScaledVector(bend, height);
    return result;
  } };
}

/** out = inverse(parentWorld) * desiredWorld; out must differ from both inputs.
 * Keep the matrix (matrixAutoUpdate=false) if nonuniform parent scale produces
 * shear; decomposing it to position/quaternion/scale loses that shear.
 */
export function worldToLocalMatrix(desiredWorld, parentWorld, out) {
  if (out === desiredWorld || out === parentWorld) throw new Error('out must not alias an input');
  if (parentWorld.determinant() === 0) throw new Error('singular parent matrix');
  return out.copy(parentWorld).invert().multiply(desiredWorld);
}
